<template>
  <div
    :class="[contentToTailwindClasses({ backgroundColor })]"
    class="newsletter-registration-component relative overflow-hidden rounded-xl"
  >
    <DotPattern v-if="dotPattern" :variant="dotPattern" />
    <div
      :class="{
        'md:px-16 xl:w-full xl:px-12': hasImage,
      }"
      class="relative grid gap-6 px-4 py-8 md:mx-auto md:w-10/12 md:gap-y-8 md:px-0 md:py-12 xl:grid-cols-5"
    >
      <PictureElement
        v-if="hasImage"
        :class="{ 'order-last': !imageFirst && isTabletOrMobile }"
        :img="image"
        class="z-10 xl:order-first xl:col-span-2 xl:col-start-4 xl:row-start-1"
        img-class="object-cover aspect-4/3 rounded-xl overflow-hidden mx-auto"
      />
      <div
        ref="contentGroup"
        :class="[hasImage && isDesktop ? 'col-span-3' : 'col-span-full']"
        class="content-group flex flex-col gap-6"
      >
        <TransitionGroup name="content" @enter="onEnter">
          <div
            v-if="(!isAlreadyRegistered && !isSubmitted) || isEditMode()"
            class="editmode-info-box flex flex-col"
          >
            <div v-if="isEditMode()" class="editmode-text">[EDITOR VIEW] Registration Content:</div>
            <Heading
              v-if="registrationHeadline?.text"
              class="mb-4"
              color="blue"
              :tag="registrationHeadline?.tag ?? 'h2'"
            >
              {{ registrationHeadline.text }}
            </Heading>
            <Paragraph v-if="registrationSubline" class="mb-6">
              {{ registrationSubline }}
            </Paragraph>
            <NewsletterBenefits v-if="hasNodes(benefits)" :benefits="benefits" />
            <form @submit.prevent="onSubmitNewsletterRegistration()">
              <InputField
                v-model="newsletterFormState.email"
                :class="{ hidden: !!props.email }"
                :error-message="newsletterVuelidate?.email?.$errors[0]?.$message"
                :label="emailFormLabels?.emailAddressLabel"
                :placeholder="emailFormLabels?.emailInputPlaceholder"
                class="form-field-white mb-4 mt-2 block w-full md:w-96"
                name="email"
                required="required"
                type="email"
                @blur="newsletterVuelidate?.email.$touch()"
              />

              <InputField
                v-model="newsletterFormState.middlename"
                autocomplete="off"
                class="form-field-umbrella"
                label="middlename"
                name="middlename"
                placeholder="middlename"
                type="text"
              />
              <Checkbox
                v-model="newsletterFormState.consent"
                :error="!!newsletterVuelidate?.consent?.$errors[0]?.$message"
                :error-text="newsletterVuelidate?.consent?.$errors[0]?.$message"
                name="consent"
                class="text-sm"
                @change="newsletterVuelidate.consent.$touch()"
              >
                <CommonLabel path="/BWF/components/newsletter/newsletterConsent" />
              </Checkbox>
              <VueFriendlyCaptcha
                ref="captcha"
                class="mt-4"
                :sitekey="siteKey"
                :language="currentLanguageShort"
                @done="
                  (solution) => {
                    captchaSolution = solution;
                  }
                "
              />
              <button
                :disabled="!captchaSolution"
                class="btn btn-blue z-10 mt-4 md:mt-6 md:place-self-start"
              >
                <CommonLabel path="BWF/components/newsletter/submit" />
              </button>
            </form>
          </div>

          <div v-if="isEditMode() || isSubmitted" class="editmode-info-box flex flex-col">
            <p v-if="isEditMode()" class="editmode-text">[EDITOR VIEW] Confirmation Content:</p>
            <Heading
              v-if="confirmationHeadline?.text"
              class="mb-4"
              color="blue"
              :tag="confirmationHeadline?.tag ?? 'h2'"
            >
              {{ confirmationHeadline.text }}
            </Heading>
            <Paragraph v-if="confirmationSubline" class="mb-6">
              {{ confirmationSubline }}
            </Paragraph>
            <NewsletterBenefits v-if="hasNodes(confirmationSteps)" :benefits="confirmationSteps" />
          </div>

          <div v-if="isEditMode() || isAlreadyRegistered" class="editmode-info-box flex flex-col">
            <div v-if="isEditMode()" class="editmode-text">
              [EDITOR VIEW] Already Registered Content:
            </div>
            <Heading
              v-if="informationHeadline?.text"
              class="mb-4"
              color="blue"
              :tag="informationHeadline?.tag ?? 'h2'"
            >
              {{ informationHeadline.text }}
            </Heading>
            <Paragraph v-if="informationSubline" class="mb-6">
              {{ informationSubline }}
            </Paragraph>
            <NewsletterBenefits v-if="hasNodes(informationSteps)" :benefits="informationSteps" />
          </div>
        </TransitionGroup>
      </div>
    </div>

    <Transition mode="out-in" name="loading">
      <LoadingElement v-if="isLoading" class="bg-white/70" :overlay="true" />
    </Transition>
  </div>
</template>

<script setup>
import VueFriendlyCaptcha from "@somushq/vue3-friendly-captcha";
import { useVuelidate } from "@vuelidate/core";
import { Checkbox } from "~/components/clarity-ui/Checkbox";
import { Heading } from "~/components/clarity-ui/Heading";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import useBritaMiddleware from "~/composables/useBritaMiddleware";
import useCaptcha from "~/composables/useCaptcha";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import NewsletterBenefits from "~/templates/elements/NewsletterBenefits.vue";
import PictureElement from "~/templates/elements/PictureElement.vue";
import InputField from "~/templates/elements/form/InputField.vue";
import DotPattern from "~/templates/elements/DotPattern.vue";
import LoadingElement from "~/templates/elements/LoadingElement.vue";
import { isEditMode } from "~/utils/content/magnolia";
import { containsImage, hasNodes } from "~/utils/helper/magnolia";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";

const props = defineProps([
  "backgroundColor",
  "registrationHeadline",
  "registrationSubline",
  "image",
  "imageFirst",
  "benefits",
  "confirmationHeadline",
  "confirmationSubline",
  "confirmationSteps",
  "informationHeadline",
  "informationSubline",
  "informationSteps",
  "dotPattern",
  "interactionId",
  "actionId",
  "skipDoi",
  "email",
]);

const captcha = ref();
const { captchaSolution, siteKey } = useCaptcha();
const { currentLanguageShort } = useMagnoliaLanguage();

const { isDesktop, isTabletOrMobile } = useDevice();
const { user } = useUser();
const { register, getDoiState } = useBritaMiddleware();
const { pushError } = useNotifications();

const { getLabelByPath, getLabels } = useCommonLabels();

const hasImage = containsImage(props.image);

const newsletterFormState = reactive({
  email: props?.email ?? user?.value?.email ?? "",
  middlename: "",
  consent: null,
});

watch(
  () => props.email,
  () => {
    if (props?.email) {
      newsletterFormState.email = props?.email;
    }
  },
);

const contentMinHeight = ref("0px");

const { emailValidators, consentValidators, requiredValidator } = await useValidation();

const rules = computed(() => ({
  email: {
    ...requiredValidator,
    ...emailValidators,
  },
  consent: {
    ...consentValidators,
  },
}));
const newsletterVuelidate = useVuelidate(rules, newsletterFormState);

const isSubmitted = ref(false);
const isAlreadyRegistered = ref(false);
const isLoading = ref(false);

const emailFormLabels = getLabels("/BWF/shared/", ["emailAddressLabel", "emailInputPlaceholder"]);
const technicalErrorMessage = getLabelByPath("/BWF/newsletter/doi/technicalErrorMessage");

const contentGroup = ref();
onMounted(() => {
  contentMinHeight.value = `${contentGroup?.value?.offsetHeight}px`;
});
const onEnter = async (el) => {
  contentMinHeight.value = `${el.offsetHeight}px`;
};

const onSubmitNewsletterRegistration = async () => {
  isLoading.value = true;
  newsletterVuelidate.value.$touch();
  const valid = await newsletterVuelidate.value.$validate();
  if (valid) {
    try {
      const data = props?.skipDoi === true ? {} : await getDoiState(newsletterFormState.email);
      if (data?.donotbulkemail === false) {
        // user has already opted in
        isAlreadyRegistered.value = true;
        isLoading.value = false;
        return;
      }
      try {
        await register(
          newsletterFormState?.email,
          props.interactionId,
          props.actionId,
          newsletterFormState.middlename,
          {
            bwfCaptcha: captchaSolution.value,
          },
        );
        isSubmitted.value = true;
      } catch (e) {
        if (registerError?.value?.statusCode === 406) {
          // 406
          isAlreadyRegistered.value = true;
        } else if (error?.value) {
          pushError(technicalErrorMessage, e);
        }
      }
    } catch (e) {
      pushError(technicalErrorMessage);
    }
  } else {
    pushError(technicalErrorMessage);
  }
  isLoading.value = false;
};
</script>

<style scoped>
.content-group {
  @apply relative motion-safe:transition-all motion-safe:duration-1000;

  position: relative;
  min-height: v-bind(contentMinHeight);
}
</style>

<style>
.newsletter-registration-component {
  .editmode-text {
    @apply mb-4 border-b-2 text-xl font-bold text-green;
  }

  .content-enter-active,
  .content-leave-active {
    @apply motion-safe:transition-all motion-safe:duration-700;
  }

  .content-enter-from,
  .content-leave-to {
    position: absolute;
    transform: translateX(30px);
    opacity: 0;
  }

  .loading-enter-active,
  .loading-leave-active {
    @apply motion-safe:transition-opacity motion-safe:duration-500;
  }

  .loading-enter-from,
  .loading-leave-to {
    opacity: 0;
  }
}
</style>
